import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Stats from 'frontend_stats';
import throttle from 'lodash.throttle';
import loadable from '@loadable/component';
import { AppSearchEngines } from 'core/utils';
import { getSearchClient } from 'core/utils/appSearch';
import {
	Form,
	Input,
	Submit,
	SearchIcon,
} from './styles';

const SearchResults = loadable(() => import('./SearchResults'));


export default class SearchBar extends Component {
	static propTypes = {
		hitsPerPage: PropTypes.number,
		placeholder: PropTypes.string,
		submitTitle: PropTypes.string,
		searchEmptyState: PropTypes.arrayOf(
			PropTypes.shape({
				title: PropTypes.string,
				items: PropTypes.array,
			})
		),
		path: PropTypes.string,
	};

	static defaultProps = {
		hitsPerPage: 3,
		placeholder: "Find a box you'll love...",
		submitTitle: "Search subscription boxes",
	};

	constructor(props) {
		super(props);

		this.blurDelayTimeoutId = null;
		this.requestNum = 0;
		this.throttledAppSearch = throttle(this.doAppSearch, 500, { 'trailing': true, 'leading': false });

		this.state = {
			focus: false,
			focusIndex: -1,
			query: "",
			indexHits: {},
			totalHits: 0,
		};
	}

	focus = () => this.setState({ focus: true })

	blur = () => this.setState({ focus: false, focusIndex: -1 })

	normalizeAppSearchHit = (hit, engineName) => {
		switch (engineName) {
			case AppSearchEngines.LISTINGS:
				return {
					id: hit.getRaw('id'),
					listingId: hit.getRaw('listing_id'),
					slug: hit.getRaw('slug'),
					title: hit.getRaw('name'),
					description: hit.getRaw('brief_description'),
					image: hit.getRaw('image'),
					storeName: hit.getRaw('store_name'),
					engine: engineName
				};
			case AppSearchEngines.BLOG_POSTS:
				return {
					id: hit.getRaw('id'),
					title: hit.getRaw('title'),
					slug: hit.getRaw('slug'),
					image: hit.getRaw('meta_image'),
					engine: engineName
				};
			case AppSearchEngines.COLLECTIONS:
				return {
					id: hit.getRaw('id'),
					title: hit.getRaw('name'),
					slug: hit.getRaw('slug'),
					description: hit.getRaw('description'),
					image: hit.getRaw('image'),
					engine: engineName
				};
			case AppSearchEngines.CATEGORIES:
				return {
					id: hit.getRaw('id'),
					title: hit.getRaw('name'),
					slug: hit.getRaw('slug'),
					description: hit.getRaw('description'),
					image: hit.getRaw('image'),
					engine: engineName
				};
		}
	}

	doSearch = (focus = false) => {
		const urlParams = new URLSearchParams(window.location.search);

		this.throttledAppSearch(focus);
	}

	doAppSearch = (focus = false) => {
		if (this.state.query.length < 2) {
			return;
		}

		let indexHits = {
			[AppSearchEngines.LISTINGS]: [],
			[AppSearchEngines.CATEGORIES]: [],
			[AppSearchEngines.COLLECTIONS]: [],
		};

		let totalHits = 0;
		let focusIndex = -1;
		let queries = [];
		let requestNum = ++this.requestNum;

		// Search all the indices
		Object.values(AppSearchEngines).forEach(engineName => {
			let appSearchClient = getSearchClient(engineName);
			let indexOptions = {
				[AppSearchEngines.LISTINGS]: {
					filters: {
						active: ['true']
					},
					page: {
						size: 6
					}
				},
				[AppSearchEngines.COLLECTIONS]: {
					filters: {
						"all": [
							{ active: ['true'] }
						],
						"none": [
							{ "type": "5" }
						]
					},
					page: {
						size: 1
					}
				},
				[AppSearchEngines.CATEGORIES]: {
					filters: {
						deleted: ['false']
					},
					page: {
						size: 1
					}
				}
			}[engineName];

			queries.push(appSearchClient.search(
				this.state.query, indexOptions
			));

		});

		// Wait for all results to be available
		Promise.all(queries).then(resultLists => {
			resultLists.forEach(resultList => {
				// Skip engines that have not been setup / initialized (they dont have the metadata in their response)
				if (!resultList.info.meta.engine)
					return;

				if (requestNum != this.requestNum) {
					// Discard results from old query
					return;
				}

				indexHits[resultList.info.meta.engine.name] = resultList.results.map(h => this.normalizeAppSearchHit(h, resultList.info.meta.engine.name));

				totalHits += resultList.results.length;
			});

			if (requestNum == this.requestNum) {
				this.setState({ indexHits, totalHits, focusIndex, focus });
			} else {
				// Result is discarded because it's a response to a request that's not the most
				// recent one.
			}

		}).catch(error => {
			// If one engine fail, we'll get no results on the succeeding ones either.
			console.log(`error: ${error}`);
		});

	};

	onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const { query } = this.state;

		Stats.trackGeneric('mp_search', { step_name: 'input' });

		window.location.href = `/search/${query}`;

		return false;
	};

	handleKeyNavigation = (e) => {
		let { keyCode } = e;
		if (keyCode !== 40 && keyCode !== 38 && keyCode !== 27) return true;
		e.preventDefault();

		if (keyCode === 27) {
			// ESC
			return this.blur();
		}

		let { focusIndex, totalHits } = this.state;
		if (keyCode === 40) {
			// down press
			focusIndex = Math.min(focusIndex + 1, totalHits - 1);
		} else {
			// up press
			focusIndex = Math.max(focusIndex - 1, -1);
		}

		this.setState({ focusIndex });
	};

	handleChange = ({ target }) => {
		const query = target.value;
		this.setState({ query }, () => this.doSearch(true));
	};

	render() {
		const { placeholder, submitTitle, path } = this.props;

		return (
			<div
				style={{ position: 'relative', width: '100%', textAlign: 'center' }}
				onKeyDown={this.handleKeyNavigation}
				onFocus={() => {
					this.blurDelayTimeoutId && clearTimeout(this.blurDelayTimeoutId);
					this.focus();
				}}
				onBlur={() => {
					// this event also bubbles up from children
					// timeout is used to avoid false-positive blurs
					// i.e. when one child is blurred and another immediately focused
					// e.g. blur the input to focus (click) on a child link
					this.blurDelayTimeoutId = setTimeout(this.blur, 200);
				}}>
				<Form noValidate="" action="" role="search" onSubmit={this.onSubmit}>
					<Input
						type="text" //iOS Safari 15.1+ has a bug where it ignores appearance styles for any "search" input
						placeholder={placeholder}
						autoComplete="off"
						autoCorrect="off"
						autoCapitalize="off"
						spellCheck={false}
						maxLength={512}
						value={this.state.query}
						onSubmit={this.onSubmit}
						onChange={this.handleChange}
					/>
					<Submit type="submit" title={submitTitle}>
						<SearchIcon />
					</Submit>
				</Form>
				{this.state.focus && (
					<SearchResults
						focusIndex={this.state.focusIndex}
						query={this.state.query}
						indexHits={this.state.indexHits}
						totalHits={this.state.totalHits}
						searchEmptyState={this.props.searchEmptyState}
						path={path}
					/>
				)}
			</div>
		);
	}
}
